import React, { useContext, useState, useEffect } from "react";
import Button from "../../../components/ui/buttons/Button";
import ModalWrapper from "../../../components/ui/modals/ModalWrapper";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { IoClose } from "react-icons/io5";
import { getDateString } from "../../../utils/datetime";

/**
 * Modal for picking a work scope and quantity when adding or changing a scope on
 * a dispatch job. Scope options come from {@link DispatchContext}. Returns the
 * chosen scope object and quantity via `onConfirm`.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {Function} props.onConfirm `(scope, quantity) => void` on confirm.
 * @param {Function} props.onClose Close without applying a selection.
 * @param {Object} [props.job=null] Job the scope belongs to (filters options).
 * @param {Date} [props.date=null] Dispatch date for the selection.
 * @param {Object} [props.initialScope=null] Pre-selected scope.
 * @param {number} [props.initialQuantity=null] Pre-filled quantity.
 * @returns {JSX.Element|null}
 */
const SelectScopeModal = ({
  isOpen,
  onConfirm,
  onClose,
  job = null,
  date = null,
  initialScope = null,
  initialQuantity = null,
}) => {
  const { scopeObjects } = useContext(DispatchContext);
  const [selectedScope, setSelectedScope] = useState("");
  const [quantity, setQuantity] = useState("");

  useEffect(() => {
    if (initialScope) {
      setSelectedScope(initialScope.id);
    }
    if (initialQuantity) {
      setQuantity(initialQuantity);
    }
  }, [initialScope, initialQuantity]);

  if (!isOpen) return null;

  const handleSelectChange = (event) => {
    setSelectedScope(event.target.value);
  };

  const handleQuantityChange = (event) => {
    setQuantity(event.target.value);
  };

  const handleConfirm = () => {
    const scopeObject = scopeObjects.find(
      (scope) => scope.id === parseInt(selectedScope, 10)
    );
    setSelectedScope("");
    setQuantity("");
    onConfirm(scopeObject, quantity);
  };

  const handleCancel = () => {
    setSelectedScope(initialScope ? initialScope.id : "");
    setQuantity(initialQuantity ? initialQuantity : "");
    onClose();
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} closeButton={false}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-center w-full px-8 mr-6">
          {initialScope ? "Change " : "Add "} Scope{" "}
          {job ? `for ${job.number} - ${job.description}` : ""}{" "}
          {date ? `on ${getDateString(date)}` : ""}
        </h2>
        <button
          onClick={handleCancel}
          className="absolute top-3 right-3 p-2 hover:text-gray-800 transition-colors ease-in-out duration-200"
        >
          <IoClose className="text-xl text-gray-600 m-auto" />
        </button>
      </div>
      <div className="text-center">
        <select
          value={selectedScope}
          onChange={handleSelectChange}
          className="mt-4 w-full p-2 border rounded"
        >
          <option value="" disabled>
            Select a scope
          </option>
          {scopeObjects.map((scopeObject) => (
            <option key={scopeObject.id} value={scopeObject.id}>
              {scopeObject.title}
            </option>
          ))}
        </select>
        <input
          type="number"
          inputMode="decimal"
          step="1"
          value={quantity}
          placeholder="Quantity"
          onChange={handleQuantityChange}
          min="1"
          className="mt-4 w-full p-2 border rounded caret-black"
        />
        <div className="flex flex-row justify-between">
          <Button
            onClick={handleConfirm}
            text="Confirm"
            variant="secondary"
            className="mt-4 w-full mr-1"
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SelectScopeModal;
